import {
  Boxes,
  LayoutGrid,
  House,
  Users,
  Calendar,
  FolderOpen,
  Wallet,
  FileText,
  Pill,
  Camera,
  Sparkles,
  Package,
  Building2,
  Layers,
  ClipboardCheck,
  Settings,
  Hospital,
  UserRound,
} from "lucide-react";
import type { FeatureKey, PermissionKey } from "@bookdental/schemas";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";

export type OrganizationType = "DENTAL_OFFICE" | "DENTAL_LAB" | "RADIOLOGY_CENTER";

export type NavItem = {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Cor accent (hex) usada no estilo "colorful". Fallback: azul da marca. */
  accent?: string;
  /** Some da sidebar pra quem não tem essa permissão na organização ativa (issue #10) — retrofit
   *  de UI por permissão: tela some, não só bloqueia a ação no clique. Lista = basta uma delas. */
  requiresPermission?: PermissionKey | PermissionKey[];
  /** Some da sidebar quando a feature não está disponível pra organização ativa — ver
   *  apps/app/src/lib/use-feature.ts. */
  requiresFeature?: FeatureKey;
  /** Só aparece pra quem tem esse papel na organização ativa. */
  requiresRole?: "OWNER";
  /** Alvo do tour global — marca esse link de menu como um "passo" apontável pelo Joyride sem
   *  precisar navegar até a página de verdade (usado pelo tour global no lugar de um elemento
   *  específico de dentro da tela, evitando trocar de rota no meio do tour). */
  dataTour?: string;
  /** Frase curta do cartão na página Menu. */
  description?: string;
  /** Outras rotas em que o item fica marcado (o hub Minha clínica nas páginas que ele reúne). */
  ativoEm?: string[];
  /** Some quando a organização ativa tem um desses tipos. */
  ocultoEm?: OrganizationType[];
};

export const itemAtivo = (item: NavItem, pathname: string) =>
  [item.href, ...(item.ativoEm ?? [])].some((h) => pathname === h || pathname.startsWith(h + "/"));

export type NavGroup = {
  /** Identidade do grupo na ordem salva pelo usuário (`User.sidebarOrder`). Não pode mudar depois
   *  de existir: `label` é opcional e traduzível, e o índice muda a cada grupo novo — os dois
   *  reembaralhariam o menu de quem já arrastou. */
  key: string;
  label?: string;
  items: NavItem[];
  /** Se preenchido, o grupo só aparece quando a clínica ativa tem pelo menos
   *  um desses types. Sem filtro = grupo universal (ex: Início, Apps Pro). */
  organizationTypes?: OrganizationType[];
  /** Oculta o grupo incondicionalmente, independente do tipo da organização. */
  hidden?: boolean;
  /** Só aparece quando a organização ativa é a pessoal. */
  personalOnly?: boolean;
  /** Só aparece quando a organização ativa é de equipe (não pessoal). */
  teamOnly?: boolean;
};

export const navGroups: NavGroup[] = [
  // Início — universal, sem header. "Dashboard" (legado) fica oculto do menu por enquanto —
  // rota ainda existe (App.tsx) pra quem tiver link direto, só não aparece mais aqui.
  {
    key: "inicio",
    items: [
      {
        label: "Início",
        href: "/inicio", description: "Resumo do dia: agenda, pedidos e o que precisa da sua atenção.",
        icon: House,
        accent: "#2457ff",
        dataTour: "sidebar-inicio",
      },
      {
        label: "Menu",
        href: "/menu",
        icon: LayoutGrid,
        accent: "#6366f1",
        dataTour: "sidebar-menu",
      },
    ],
  },

  // ─── Gestão (conta pessoal) ────
  {
    key: "gestao-pessoal",
    label: "Gestão",
    personalOnly: true,
    items: [
      { label: "Organizações", href: "/organizacoes", description: "As organizações em que você está e as que você criou.", icon: Building2, accent: "#2457ff", dataTour: "sidebar-organizacoes" },
      { label: "Minha conta", href: "/conta", description: "Seus dados, senha, notificações e aparência.", icon: UserRound, accent: "#64748b", dataTour: "sidebar-conta" },
      { label: "Configurações", href: "/configuracoes", description: "Endereço, área de atuação e agenda da sua organização.", icon: Settings, accent: "#64748b", requiresPermission: "settings.manage", dataTour: "sidebar-configuracoes" },
    ],
  },

  // ─── Clínica odontológica ────────────────────────────────────
  {
    key: "clinica",
    label: "Clínica",
    organizationTypes: ["DENTAL_OFFICE"],
    items: [
      { label: "Pacientes", href: "/pacientes", description: "Cadastro, prontuário, arquivos e histórico de cada paciente.", icon: Users, accent: "#14b8a6", requiresPermission: "patients.view", dataTour: "sidebar-pacientes" },
      { label: "Agenda", href: "/agenda", description: "Consultas por dia, semana ou mês, de toda a equipe.", icon: Calendar, accent: "#0ea5e9", requiresPermission: "agenda.view", dataTour: "sidebar-agenda" },
      { label: "Minha clínica", href: "/clinica", description: "Salas, equipe e, em breve, o estoque da clínica.", icon: Hospital, accent: "#7c3aed", requiresPermission: ["agenda.view", "team.manage"], ativoEm: ["/salas", "/equipe"], dataTour: "sidebar-minha-clinica" },
      { label: "Prótese", href: "/protese", description: "Pedidos aos laboratórios, prazos e laboratórios parceiros.", icon: CoroaDentariaIcon, accent: "#d946ef", requiresPermission: "prosthesis.view", dataTour: "sidebar-protese" },
      { label: "Procedimentos", href: "/procedimentos", description: "Os procedimentos que vocês fazem, com duração, cor e preço.", icon: Package, accent: "#06b6d4", requiresFeature: "clinicServices", dataTour: "sidebar-procedimentos" },
      { label: "Receituário", href: "/receituario", description: "Receitas e atestados prontos para imprimir ou enviar.", icon: Pill, accent: "#f59e0b", requiresFeature: "receituario" },
      { label: "Arquivos", href: "/arquivos", description: "Pastas e documentos da clínica, compartilhados com quem precisa.", icon: FolderOpen, accent: "#10b981", requiresPermission: "files.manage", dataTour: "sidebar-arquivos-clinica" },
    ],
  },

  // ─── Laboratório ─────────────────────────────────────────────
  {
    key: "laboratorio",
    label: "Laboratório",
    organizationTypes: ["DENTAL_LAB"],
    items: [
      { label: "Cadastros", href: "/lab/cadastros", description: "Dentistas, clínicas, laboratórios parceiros e fornecedores.", icon: Users, accent: "#14b8a6", requiresPermission: "partners.manage", dataTour: "sidebar-cadastros" },
      { label: "Catálogo", href: "/lab/catalogo", description: "Serviços, materiais, cores, equipamentos e preços do laboratório.", icon: Layers, accent: "#d946ef", requiresPermission: ["labCatalog.manage", "prices.view"], dataTour: "sidebar-catalogo" },
      { label: "Estoque", href: "/lab/estoque", description: "O que comprar, saldos, movimentos e o cadastro de insumos.", icon: Boxes, accent: "#0ea5e9", requiresPermission: "stock.manage", dataTour: "sidebar-estoque" },
      { label: "Pedidos", href: "/lab/pedidos", description: "Pedidos recebidos das clínicas: aceitar, acompanhar e concluir.", icon: ClipboardCheck, accent: "#22c55e", requiresPermission: "production.view", dataTour: "sidebar-pedidos" },
      { label: "Produção", href: "/lab/producao", description: "Os trabalhos em andamento, por estágio ou em planilha.", icon: FileText, accent: "#f59e0b", requiresPermission: "production.view", dataTour: "sidebar-producao" },
      { label: "Financeiro", href: "/financeiro", description: "Receita, custos, margem, a receber e a pagar do laboratório.", icon: Wallet, accent: "#22c55e", requiresPermission: "finance.view", dataTour: "sidebar-financeiro" },
      { label: "Arquivos", href: "/lab/arquivos", description: "Pastas e arquivos do laboratório, incluindo os enviados pelas clínicas.", icon: FolderOpen, accent: "#10b981", requiresPermission: "files.manage", dataTour: "sidebar-arquivos-lab" },
    ],
  },

  // ─── Radiologia ──────────────────────────────────────────────
  // Oculto por enquanto — MVP é só Clínica+Laboratório.
  {
    key: "radiologia",
    label: "Radiologia",
    organizationTypes: ["RADIOLOGY_CENTER"],
    hidden: true,
    items: [
      { label: "Dentistas", href: "/radio/dentistas", description: "Os dentistas que enviam pacientes para exame.", icon: Users, accent: "#14b8a6" },
      { label: "Exames", href: "/radio/exames", description: "Exames realizados, laudos e envio ao dentista.", icon: Camera, accent: "#06b6d4" },
      { label: "Procedimentos", href: "/procedimentos", description: "Os procedimentos que vocês fazem, com duração, cor e preço.", icon: Package, accent: "#06b6d4", requiresFeature: "clinicServices" },
    ],
  },

  // ─── Gestão ──────────────────────────────────────────────────
  // Oculto por enquanto — MVP é só Clínica+Laboratório.
  {
    key: "financeiro",
    label: "Gestão",
    hidden: true,
    items: [
      { label: "Financeiro", href: "/financeiro", description: "Entradas, saídas e extratos da organização.", icon: Wallet, accent: "#22c55e" },
    ],
  },

  // ─── Apps Pro ────────────────────────────────────────────────
  {
    key: "apps-pro",
    items: [
      { label: "Apps Pro", href: "/apps-pro", description: "Aplicativos profissionais com IA.", icon: Sparkles, accent: "#ec4899", dataTour: "sidebar-apps-pro" },
    ],
  },

  // ─── Equipe e Configurações (organização de equipe) ────
  // Sem título e no fim: na clínica sobra só Configurações, que se abre de vez em quando.
  {
    key: "gestao-equipe",
    teamOnly: true,
    items: [
      // Na clínica a Equipe mora dentro de Minha clínica (pedido do Jun, 29/10).
      { label: "Equipe", href: "/equipe", description: "Membros, convites e o que cada um pode fazer na organização.", icon: Users, accent: "#0ea5e9", requiresPermission: "team.manage", dataTour: "sidebar-equipe", ocultoEm: ["DENTAL_OFFICE"] },
      { label: "Configurações", href: "/configuracoes", description: "Dados, endereço, expediente, agenda e permissões da organização.", icon: Settings, accent: "#64748b", requiresPermission: ["settings.manage", "patients.configure"], dataTour: "sidebar-configuracoes" },
    ],
  },
];
